import React, {useEffect, useState, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {deleteTagType, getTagsTypes} from '../../../../features/types';
import { SVGS } from '../../../../assets/images';
import {TiminatorTagType, TiminatorType} from '../../../../features/types/types';
import IconTextLarge from '../../../../components/shared/large-icon-text';
import TiminatorTableV2 from '../../../../components/shared/table-v2';
import { useNavigate } from 'react-router-dom';
import PageWrapperV2 from '../../../../components/shared/page-wrapper-v2';
import PageHeader from '../../../../components/shared/page-header';
import { useIntl } from 'react-intl';
import CreateNewButtonV2 from '../../../../components/shared/create-new-button-v2';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import PageBody from '../../../../components/shared/page-body';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useMediaQuery } from 'react-responsive';
import { TypeFilter, DEFAULT_TYPE_FILTER_STATE } from '../type-filter';
import { DeleteIconButton } from '../../../../components/shared/delete-icon-button';
import PageFooter from '../../../../components/shared/page-footer';
import { TypeHeader } from '../type-header';
import { useTagTypesTable } from './hook/useTagTypesTable';
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination';
import { canManageTagTypes, canSeeTagTypesSection } from '../../../../features/profile/entityPermissions';
import { getVisibleTypeTabs } from '../../tabs';

export default function TagTypes() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [selectedTagType, setSelectedTagType] = useState<TiminatorTagType | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [search, setSearch] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 1000  });

    const tagTypesState = useAppSelector(store => store.types.tags);
    const typesState = useAppSelector(store => store.types);
    const profile = useAppSelector(store => store.profile);
    const hasMore = tagTypesState.items.length < tagTypesState.total;
    const canEditTagTypes = canManageTagTypes(profile);
    const canViewTagTypes = canSeeTagTypesSection(profile, tagTypesState.permissions);

    const fetchTagTypesPage = useCallback((page: number) => {
        return dispatch(getTagsTypes(page)).unwrap();
    }, [dispatch]);

    const { loadInitial, loadNext, isLoadingMore, isLoadingInitial, reset } = useInfinitePagination({
        hasMore,
        fetchPage: fetchTagTypesPage,
    });

    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const handleMobileHeaderClose = useCallback(() => {
        handleFilterChange(DEFAULT_TYPE_FILTER_STATE);
    }, [handleFilterChange]);

    useEffect(() => {
        if (!canViewTagTypes) {
            const availableTab = getVisibleTypeTabs(profile, {
                project: typesState.projects.permissions,
                shift: typesState.shifts.permissions,
                absence: typesState.absences.permissions,
                tag: tagTypesState.permissions,
            })[0];

            if (availableTab) {
                navigate(availableTab.path, { replace: true });
            }
        }
    }, [canViewTagTypes, navigate, profile, tagTypesState.permissions, typesState.absences.permissions, typesState.projects.permissions, typesState.shifts.permissions]);

    useEffect(() => {
        if (!canViewTagTypes) {
            return;
        }

        loadInitial().catch(() => { /* handled elsewhere */ });
    }, [canViewTagTypes, loadInitial]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const { columns, data, isEmpty } = useTagTypesTable(
        search,
        selectedStatus,
        isMobile
    );
    const showEmptyState = !isLoadingInitial && isEmpty;

    if (!canViewTagTypes) {
        return null;
    }

    const enhancedColumns = columns
        .filter(col => canEditTagTypes || col.id !== 'deleteButton')
        .map(col => {
            if (col.id === 'deleteButton') {
                return {
                    ...col,
                    format: (rowData: TiminatorType) => (
                        <DeleteIconButton
                            onClick={() => {
                                setSelectedTagType(rowData);
                                setOpen(true);
                            }}
                        />
                    ),
                };
            }
            return col;
        });

    const handleDeleteCancel = () => {
        setOpen(false);
        setSelectedTagType(null);
    };

    const handleDeleteConfirm = () => {
        if (selectedTagType) {
            dispatch(deleteTagType(selectedTagType.id)).unwrap().then(() => {
                reset();
                loadInitial().catch(() => { /* handled elsewhere */ });
            });
        }
        handleDeleteCancel();
    };

    const handleCreateNew = () => {
        if (!canEditTagTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_TAG_TYPE);
    }

    return <>
        <PageWrapperV2>
            <PageHeader>
                <TypeHeader
                    isEmpty={showEmptyState}
                    isFilterOpen={mobileView === 'filter'}
                    onMobileFilterClick={handleMobileFilterClick}
                    onCreateNew={handleCreateNew}
                    canCreate={canEditTagTypes}
                    onMobileFilterClose={handleMobileHeaderClose}
                    loading={isLoadingInitial}
                />
            </PageHeader>
            <PageBody justifyContent={showEmptyState ? 'center' : 'flex-start'} alignItems='stretch'>
                {   
                    !showEmptyState
                        ? (
                            <TypeFilter
                                search={search}
                                selectedStatus={selectedStatus}
                                isMobile={isMobile}
                                mobileView={mobileView}
                                onFilterChange={handleFilterChange}
                                onMobileClose={handleMobileFilterClose}
                            >
                                <TiminatorTableV2 
                                    columns={enhancedColumns}
                                    data={data}
                                    hasMore={hasMore}
                                    onLoadMore={loadNext}
                                    isLoadingMore={isLoadingMore} />
                            </TypeFilter>
                        )
                        : 
                        <IconTextLarge 
                            text={intl.formatMessage({id: Lx.Type.NO_TAG_TYPES})} 
                            subtext={intl.formatMessage({id: Lx.Type.CREATE_AND_USE_TAG_TYPE})} 
                            icon={<SVGS.TypesIcon />} />
                }
            </PageBody>
            {
                isMobile && mobileView === 'list' && canEditTagTypes &&
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateNew} />
                    </PageFooter>
            }
        </PageWrapperV2>
        <DeleteDialog 
            open={open} 
            onCancel={handleDeleteCancel} 
            onConfirmDelete={handleDeleteConfirm}
            deleteHeader={intl.formatMessage({id: Lx.Type.DELETE_TAG_TYPE})}
            deleteMessage={intl.formatMessage({id: Lx.Type.DELETE_TAG_TYPE_MESSAGE}, { name: selectedTagType?.name })}
            title={intl.formatMessage({id: Lx.General.TAG_TYPE})}  />
    </>
}
